import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CarHireBooking = () => {
    const [searchData, setSearchData] = useState({
        pickupLocation: '',
        dropoffLocation: '',
        pickupDate: '',
        dropoffDate: '',
        pickupTime: '10:00',
        dropoffTime: '10:00',
        driverAge: 30
    });

    const [locations, setLocations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sameLocation, setSameLocation] = useState(true);

    // Fetch rental locations on component mount
    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const response = await axios.get('/api/car-hire/rental-locations/');
            const locationsData = response.data.results || response.data || [];
            setLocations(Array.isArray(locationsData) ? locationsData : []);
        } catch (error) {
            setLocations([]);
            console.error('Error fetching locations:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSameLocationChange = (e) => {
        setSameLocation(e.target.checked);
        if (e.target.checked) {
            setSearchData(prev => ({
                ...prev,
                dropoffLocation: prev.pickupLocation
            }));
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            const searchParams = {
                pickup_location_id: searchData.pickupLocation,
                dropoff_location_id: sameLocation ? searchData.pickupLocation : searchData.dropoffLocation,
                pickup_date: searchData.pickupDate,
                dropoff_date: searchData.dropoffDate,
                pickup_time: searchData.pickupTime,
                dropoff_time: searchData.dropoffTime,
                driver_age: searchData.driverAge
            };

            const response = await axios.get('/api/car-hire/search/', { params: searchParams });
            
            // Here you would typically navigate to results page or update state
            console.log('Search results:', response.data);
            
        } catch (error) {
            setLocations([]);
            console.error('Search error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="booking__area" id="search">
            <div className="container">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="booking__area-form">
                            <div className="booking__area-title">
                                <h3>Search Car Rentals</h3>
                                <p>Find and compare the best car rental deals</p>
                            </div>
                            
                            <form onSubmit={handleSearch}>
                                {/* Main search row with all fields in one line */}
                                <div className="row align-items-end car-search-row">
                                    {/* Pickup Location */}
                                    <div className="col-lg-2 col-md-6 col-sm-6">
                                        <div className="booking__area-form-item compact">
                                            <i className="fas fa-map-marker-alt"></i>
                                            <div className="booking__area-form-item-content">
                                                <span>Pickup Location</span>
                                                <select 
                                                    name="pickupLocation" 
                                                    value={searchData.pickupLocation}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select pickup location</option>
                                                    {locations.map(location => (
                                                        <option key={location.id} value={location.id}>
                                                            {location.name}, {location.city}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Pickup Date */}
                                    <div className="col-lg-2 col-md-6 col-sm-6">
                                        <div className="booking__area-form-item compact">
                                            <i className="fas fa-calendar-alt"></i>
                                            <div className="booking__area-form-item-content">
                                                <span>Pickup Date</span>
                                                <input 
                                                    type="date" 
                                                    name="pickupDate"
                                                    value={searchData.pickupDate}
                                                    onChange={handleInputChange}
                                                    min={new Date().toISOString().split('T')[0]}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Pickup Time */}
                                    <div className="col-lg-1 col-md-6 col-sm-6">
                                        <div className="booking__area-form-item compact">
                                            <i className="fas fa-clock"></i>
                                            <div className="booking__area-form-item-content">
                                                <span>Pickup Time</span>
                                                <select 
                                                    name="pickupTime"
                                                    value={searchData.pickupTime}
                                                    onChange={handleInputChange}
                                                >
                                                    {Array.from({length: 24}, (_, i) => {
                                                        const hour = i.toString().padStart(2, '0');
                                                        return (
                                                            <option key={i} value={`${hour}:00`}>
                                                                {hour}:00
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dropoff Date */}
                                    <div className="col-lg-2 col-md-6 col-sm-6">
                                        <div className="booking__area-form-item compact">
                                            <i className="fas fa-calendar-alt"></i>
                                            <div className="booking__area-form-item-content">
                                                <span>Dropoff Date</span>
                                                <input 
                                                    type="date" 
                                                    name="dropoffDate"
                                                    value={searchData.dropoffDate}
                                                    onChange={handleInputChange}
                                                    min={searchData.pickupDate || new Date().toISOString().split('T')[0]}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dropoff Time */}
                                    <div className="col-lg-1 col-md-6 col-sm-6">
                                        <div className="booking__area-form-item compact">
                                            <i className="fas fa-clock"></i>
                                            <div className="booking__area-form-item-content">
                                                <span>Dropoff Time</span>
                                                <select 
                                                    name="dropoffTime"
                                                    value={searchData.dropoffTime}
                                                    onChange={handleInputChange}
                                                >
                                                    {Array.from({length: 24}, (_, i) => {
                                                        const hour = i.toString().padStart(2, '0');
                                                        return (
                                                            <option key={i} value={`${hour}:00`}>
                                                                {hour}:00
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Driver Age */}
                                    <div className="col-lg-2 col-md-6 col-sm-6">
                                        <div className="booking__area-form-item compact">
                                            <i className="fas fa-user"></i>
                                            <div className="booking__area-form-item-content">
                                                <span>Driver Age</span>
                                                <select 
                                                    name="driverAge"
                                                    value={searchData.driverAge}
                                                    onChange={handleInputChange}
                                                >
                                                    {Array.from({length: 10}, (_, i) => {
                                                        const age = (i + 2) * 10 + (i === 0 ? 5 : 0); // 25, 30, 40, 50, etc.
                                                        return (
                                                            <option key={age} value={age}>
                                                                {age} years old
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Search Button */}
                                    <div className="col-lg-2 col-md-6 col-sm-6">
                                        <div className="booking__area-form-item compact">
                                            <button type="submit" className="btn-one w-100 search-btn-compact" disabled={isLoading}>
                                                {isLoading ? (
                                                    <>
                                                        <i className="fas fa-spinner fa-spin"></i> Searching...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fas fa-search"></i> Search Cars
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Secondary row for additional options */}
                                <div className="row mt-3">
                                    {/* Return to same location checkbox */}
                                    <div className="col-lg-6 col-md-6">
                                        <div className="booking__area-form-item-small">
                                            <label className="d-flex align-items-center same-location-checkbox">
                                                <input 
                                                    type="checkbox" 
                                                    checked={sameLocation}
                                                    onChange={handleSameLocationChange}
                                                    className="me-2"
                                                />
                                                Return to same location
                                            </label>
                                        </div>
                                    </div>

                                    {/* Dropoff Location - only show if different location selected */}
                                    {!sameLocation && (
                                        <div className="col-lg-6 col-md-6">
                                            <div className="booking__area-form-item-small">
                                                <i className="fas fa-map-marker-alt"></i>
                                                <div className="booking__area-form-item-content">
                                                    <span>Dropoff Location</span>
                                                    <select 
                                                        name="dropoffLocation" 
                                                        value={searchData.dropoffLocation}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="">Select dropoff location</option>
                                                        {locations.map(location => (
                                                            <option key={location.id} value={location.id}>
                                                                {location.name}, {location.city}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CarHireBooking;
